// Core Web Vitals monitoring and optimization utilities
import React from 'react';
import { trackEvent } from '@/utils/analytics';

export const reportCoreWebVitals = () => {
  // Report LCP (Largest Contentful Paint)
  if ('PerformanceObserver' in window) {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          console.log('LCP:', entry.startTime);
          // Use centralized trackEvent which respects geo-blocking
          trackEvent('timing_complete', {
            name: 'LCP',
            value: Math.round(entry.startTime)
          });
        }
      }
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  }
};

// Preload critical resources
export const preloadCriticalResources = () => {
  const criticalImages = [
    '/lovable-uploads/b74a38e9-6502-4630-8f9b-6ffebcce59be.png',
    '/lovable-uploads/bc9cedad-de15-492c-adcf-e0b3a3c776ce.png'
  ];

  criticalImages.forEach(src => {
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = src;
    link.fetchPriority = 'high';
    document.head.appendChild(link);
  });
};

// Lazy load non-critical components
export const lazyLoadComponent = (importFn: () => Promise<any>) => {
  return React.lazy(() => 
    importFn().then(module => ({
      default: module.default || module
    }))
  );
};

// Optimize images for Core Web Vitals
export const optimizeImage = (src: string, options: {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif' | 'jpg' | 'png';
} = {}) => {
  const { width, height, quality = 85, format = 'webp' } = options;
  
  // For production, you might use a CDN service like Cloudinary or ImageKit
  // For now, returning the original src
  return src;
};

// Mobile-specific lazy loading with intersection observer
export const lazyLoadMobile = (threshold = '200px') => {
  if ('IntersectionObserver' in window) {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              observer.unobserve(img);
            }
          }
        });
      },
      { 
        rootMargin: threshold,
        threshold: 0.01
      }
    );
    
    return observer;
  }
  
  return null;
};

// Monitor and report Web Vitals
export const initWebVitalsReporting = () => {
  // CLS (Cumulative Layout Shift)
  let clsValue = 0;
  let clsEntries: PerformanceEntry[] = [];

  if ('PerformanceObserver' in window) {
    const clsObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (!(entry as any).hadRecentInput) {
          const firstSessionEntry = clsEntries[0];
          const lastSessionEntry = clsEntries[clsEntries.length - 1];

          if (!firstSessionEntry || 
              entry.startTime - lastSessionEntry.startTime < 1000 ||
              entry.startTime - firstSessionEntry.startTime < 5000) {
            clsEntries.push(entry);
            clsValue += (entry as any).value;
          }
        }
      }
    });
    
    clsObserver.observe({ entryTypes: ['layout-shift'] });

    // Report CLS on page unload using centralized trackEvent (respects geo-blocking)
    window.addEventListener('beforeunload', () => {
      if (clsValue > 0) {
        trackEvent('timing_complete', {
          name: 'CLS',
          value: Math.round(clsValue * 1000)
        });
      }
    });
  }

  // FID (First Input Delay)
  if ('PerformanceObserver' in window) {
    const fidObserver = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        const fidValue = (entry as any).processingStart - entry.startTime;
        console.log('FID:', fidValue);
        // Use centralized trackEvent which respects geo-blocking
        trackEvent('timing_complete', {
          name: 'FID',
          value: Math.round(fidValue)
        });
      }
    });
    fidObserver.observe({ entryTypes: ['first-input'] });
  }
};
